import React, { useCallback, useState, useRef, Component } from 'react';
import { ReactFlow, ReactFlowProvider, MiniMap, Controls, Background, useNodesState, useEdgesState, addEdge, Connection, Edge, Node, Panel, NodeTypes, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { MessageSquareIcon, DatabaseIcon, ServerIcon, MailIcon, ClockIcon, FileTextIcon, BrainIcon, CodeIcon, GlobeIcon, ShieldIcon, PlusIcon, SaveIcon, PlayIcon, SettingsIcon, AlertTriangleIcon, CheckCircleIcon, XCircleIcon, CopyIcon, ArrowRightIcon, SearchIcon, ChevronRightIcon, FolderIcon, StarIcon, Trash2Icon } from 'lucide-react';
// Node types
const nodeTypesList = [{
  id: 'trigger',
  label: 'Trigger',
  icon: <ClockIcon size={20} data-id="element-2465" />,
  color: '#f59e0b',
  bgColor: '#fef3c7',
  borderColor: '#f59e0b',
  description: 'Start your workflow with a trigger'
}, {
  id: 'llm',
  label: 'AI Model',
  icon: <BrainIcon size={20} data-id="element-2466" />,
  color: '#8b5cf6',
  bgColor: '#f3e8ff',
  borderColor: '#8b5cf6',
  description: 'Process data with an AI model'
}, {
  id: 'api',
  label: 'API Request',
  icon: <GlobeIcon size={20} data-id="element-2467" />,
  color: '#3b82f6',
  bgColor: '#dbeafe',
  borderColor: '#3b82f6',
  description: 'Make external API calls'
}, {
  id: 'database',
  label: 'Database',
  icon: <DatabaseIcon size={20} data-id="element-2468" />,
  color: '#10b981',
  bgColor: '#d1fae5',
  borderColor: '#10b981',
  description: 'Interact with your database'
}, {
  id: 'email',
  label: 'Email',
  icon: <MailIcon size={20} data-id="element-2469" />,
  color: '#ef4444',
  bgColor: '#fee2e2',
  borderColor: '#ef4444',
  description: 'Send email notifications'
}, {
  id: 'code',
  label: 'Code',
  icon: <CodeIcon size={20} data-id="element-2470" />,
  color: '#6b7280',
  bgColor: '#f3f4f6',
  borderColor: '#6b7280',
  description: 'Run custom code'
}, {
  id: 'document',
  label: 'Document',
  icon: <FileTextIcon size={20} data-id="element-2471" />,
  color: '#eab308',
  bgColor: '#fef9c3',
  borderColor: '#eab308',
  description: 'Process documents and files'
}, {
  id: 'security',
  label: 'Security',
  icon: <ShieldIcon size={20} data-id="element-2472" />,
  color: '#6366f1',
  bgColor: '#e0e7ff',
  borderColor: '#6366f1',
  description: 'Add security checks and validations'
}];
// Workflow templates
const workflowTemplates = [{
  id: 'guardrail-email-alert',
  name: 'GuardRail Email Alert',
  description: 'Alert system for harmful content detection',
  icon: <AlertTriangleIcon size={20} data-id="element-2473" />,
  color: '#ef4444',
  nodes: [{
    id: 'trigger-guardrail',
    type: 'custom',
    position: {
      x: 250,
      y: 100
    },
    data: {
      ...nodeTypesList.find(type => type.id === 'trigger'),
      name: 'Harmful Content Detected',
      id: 'trigger-guardrail'
    }
  }, {
    id: 'llm-verification',
    type: 'custom',
    position: {
      x: 250,
      y: 250
    },
    data: {
      ...nodeTypesList.find(type => type.id === 'llm'),
      name: 'Verify Content Severity',
      id: 'llm-verification'
    }
  }, {
    id: 'security-check',
    type: 'custom',
    position: {
      x: 250,
      y: 400
    },
    data: {
      ...nodeTypesList.find(type => type.id === 'security'),
      name: 'Severity Decision',
      id: 'security-check'
    }
  }, {
    id: 'email-tech-team',
    type: 'custom',
    position: {
      x: 100,
      y: 550
    },
    data: {
      ...nodeTypesList.find(type => type.id === 'email'),
      name: 'Email Tech Team',
      id: 'email-tech-team'
    }
  }, {
    id: 'email-user-warning',
    type: 'custom',
    position: {
      x: 400,
      y: 550
    },
    data: {
      ...nodeTypesList.find(type => type.id === 'email'),
      name: 'Notify User',
      id: 'email-user-warning'
    }
  }],
  edges: [{
    id: 'trigger-to-llm',
    source: 'trigger-guardrail',
    target: 'llm-verification',
    animated: true,
    style: {
      stroke: '#999',
      strokeWidth: 2
    }
  }, {
    id: 'llm-to-security',
    source: 'llm-verification',
    target: 'security-check',
    animated: true,
    style: {
      stroke: '#999',
      strokeWidth: 2
    }
  }, {
    id: 'security-to-tech',
    source: 'security-check',
    target: 'email-tech-team',
    animated: true,
    style: {
      stroke: '#ef4444',
      strokeWidth: 2
    },
    label: 'High Severity'
  }, {
    id: 'security-to-user',
    source: 'security-check',
    target: 'email-user-warning',
    animated: true,
    style: {
      stroke: '#3b82f6',
      strokeWidth: 2
    },
    label: 'Low Severity'
  }]
}, {
  id: 'data-processing',
  name: 'Data Processing Pipeline',
  description: 'Extract, transform, and load data workflow',
  icon: <DatabaseIcon size={20} data-id="element-2474" />,
  color: '#10b981',
  nodes: [],
  edges: []
}, {
  id: 'content-moderation',
  name: 'Content Moderation',
  description: 'AI-powered content moderation workflow',
  icon: <ShieldIcon size={20} data-id="element-2475" />,
  color: '#6366f1',
  nodes: [],
  edges: []
}];
// Custom Node Component
const CustomNode = ({
  data,
  id
}) => {
  return <div className="node-container" style={{
    borderColor: data.borderColor,
    backgroundColor: data.bgColor
  }} data-id="element-2476">
      <Handle type="target" position={Position.Top} style={{
      background: '#555',
      width: 10,
      height: 10
    }} id={`${id}-target`} data-id="element-2477" />
      <div className="node-header" style={{
      backgroundColor: data.color
    }} data-id="element-2478">
        <div className="node-icon" data-id="element-2479">{data.icon}</div>
        <div className="node-title" data-id="element-2480">{data.label}</div>
      </div>
      {data.name && <div className="node-name" data-id="element-2481">{data.name}</div>}
      <div className="node-footer" data-id="element-2482">
        <button className="node-button" data-id="element-2483">
          <SettingsIcon size={14} data-id="element-2484" />
        </button>
        <div className="node-id" data-id="element-2485">ID: {data.id.substring(0, 8)}</div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{
      background: '#555',
      width: 10,
      height: 10
    }} id={`${id}-source`} data-id="element-2486" />
    </div>;
};
// Initial nodes and edges
const initialNodes = [{
  id: 'trigger-1',
  type: 'custom',
  position: {
    x: 250,
    y: 100
  },
  data: {
    ...nodeTypesList.find(type => type.id === 'trigger'),
    name: 'Start Workflow',
    id: 'trigger-1'
  }
}];
const initialEdges = [];
// Main component
const FlowBuilder = () => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [activeTab, setActiveTab] = useState('elements');
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedNode, setDraggedNode] = useState(null);
  // Define nodeTypes
  const nodeTypes = {
    custom: CustomNode
  };
  const onConnect = useCallback(params => {
    setEdges(eds => addEdge({
      ...params,
      animated: true,
      style: {
        stroke: '#999',
        strokeWidth: 2
      },
      type: 'smoothstep'
    }, eds));
  }, [setEdges]);
  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);
  const onDrop = useCallback(event => {
    event.preventDefault();
    if (!reactFlowWrapper.current || !reactFlowInstance) {
      return;
    }
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const nodeType = event.dataTransfer.getData('application/reactflow');
    if (!nodeType) {
      return;
    }
    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top
    });
    const selectedType = nodeTypesList.find(type => type.id === nodeType);
    if (!selectedType) {
      return;
    }
    const newNode = {
      id: `${nodeType}-${Date.now()}`,
      type: 'custom',
      position,
      data: {
        ...selectedType,
        name: `${selectedType.label} ${nodes.filter(n => n.data.label === selectedType.label).length + 1}`,
        id: `${nodeType}-${Date.now()}`
      }
    };
    setNodes(nds => nds.concat(newNode));
    setDraggedNode(null);
  }, [reactFlowInstance, nodes, setNodes]);
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
    setDraggedNode(nodeType);
  };
  const onDragEnd = () => {
    setDraggedNode(null);
  };
  // Clear canvas function
  const clearCanvas = () => {
    if (window.confirm('Are you sure you want to clear the canvas? This action cannot be undone.')) {
      setNodes(initialNodes);
      setEdges(initialEdges);
    }
  };
  const loadWorkflowTemplate = templateId => {
    const template = workflowTemplates.find(t => t.id === templateId);
    if (!template) return;
    setWorkflowName(template.name);
    if (template.nodes.length > 0) {
      setNodes(template.nodes);
      setEdges(template.edges);
    }
    // Fit view after a short delay to ensure the graph has updated
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.2
        });
      }
    }, 100);
  };
  // Filter nodes based on search term
  const filteredNodeTypes = nodeTypesList.filter(type => type.label.toLowerCase().includes(searchTerm.toLowerCase()) || type.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Filter templates based on search term
  const filteredTemplates = workflowTemplates.filter(template => template.name.toLowerCase().includes(searchTerm.toLowerCase()) || template.description.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="h-screen flex flex-col text-white" data-id="element-2487">
      {/* Header */}
      <div className="flex justify-between items-center mb-4" data-id="element-2488">
        <div className="flex items-center" data-id="element-2489">
          <input type="text" value={workflowName} onChange={e => setWorkflowName(e.target.value)} className="text-xl font-bold bg-transparent border-b-2 border-transparent focus:border-blue-500 focus:outline-none text-white" data-id="element-2490" />
        </div>
        <div className="flex space-x-2" data-id="element-2491">
          <button className="flex items-center px-3 py-2 bg-[#121212] text-gray-300 rounded-md hover:bg-[#1a1a1a] transition-colors" onClick={clearCanvas} data-id="element-2492">
            <Trash2Icon size={16} className="mr-2" data-id="element-2493" />
            Clear Space
          </button>
          <button className="flex items-center px-3 py-2 bg-[#121212] text-gray-300 rounded-md hover:bg-[#1a1a1a] transition-colors" data-id="element-2494">
            <SaveIcon size={16} className="mr-2" data-id="element-2495" />
            Save
          </button>
          <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-2496">
            <PlayIcon size={16} className="mr-2" data-id="element-2497" />
            Run
          </button>
        </div>
      </div>
      {/* Main content */}
      <div className="flex flex-1 overflow-hidden bg-[#0F0F0F] border border-[#222222] rounded-lg shadow-sm" style={{
      height: 'calc(100vh - 100px)'
    }} data-id="element-2498">
        {/* Sidebar */}
        <div className="w-64 border-r border-[#222222] bg-[#0A0A0A] overflow-y-auto flex flex-col" data-id="element-2499">
          {/* Tabs */}
          <div className="border-b border-[#222222]" data-id="element-2500">
            <div className="flex" data-id="element-2501">
              <button className={`flex-1 py-3 px-4 text-sm font-medium ${activeTab === 'elements' ? 'bg-[#121212] text-blue-400 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-300'}`} onClick={() => setActiveTab('elements')} data-id="element-2502">
                Elements
              </button>
              <button className={`flex-1 py-3 px-4 text-sm font-medium ${activeTab === 'templates' ? 'bg-[#121212] text-blue-400 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-300'}`} onClick={() => setActiveTab('templates')} data-id="element-2503">
                Templates
              </button>
            </div>
          </div>
          {/* Search */}
          <div className="p-3 border-b border-[#222222]" data-id="element-2504">
            <div className="relative" data-id="element-2505">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2506">
                <SearchIcon size={16} className="text-gray-400" data-id="element-2507" />
              </div>
              <input type="text" className="pl-10 pr-4 py-2 w-full bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-white text-sm" placeholder={activeTab === 'elements' ? 'Search elements...' : 'Search templates...'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2508" />
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto" data-id="element-2509">
            {activeTab === 'elements' && <div className="p-3" data-id="element-2510">
                <h3 className="font-medium text-gray-300 mb-3 text-sm" data-id="element-2511">
                  Elements
                </h3>
                <div className="space-y-2" data-id="element-2512">
                  {filteredNodeTypes.map(type => <div key={type.id} className={`p-3 bg-[#121212] border border-[#333333] rounded-md cursor-move hover:border-blue-600 hover:shadow-sm transition-all ${draggedNode === type.id ? 'opacity-50' : ''}`} onDragStart={event => onDragStart(event, type.id)} onDragEnd={onDragEnd} draggable data-id="element-2513">
                      <div className="flex items-center" data-id="element-2514">
                        <div className="text-white p-2 rounded-md mr-3" style={{
                    backgroundColor: type.color
                  }} data-id="element-2515">
                          {type.icon}
                        </div>
                        <div data-id="element-2516">
                          <div className="font-medium text-sm text-white" data-id="element-2517">
                            {type.label}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-2518">
                            {type.description}
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
            {activeTab === 'templates' && <div className="p-3" data-id="element-2519">
                <h3 className="font-medium text-gray-300 mb-3 text-sm" data-id="element-2520">
                  Templates
                </h3>
                <div className="space-y-2" data-id="element-2521">
                  {filteredTemplates.map(template => <div key={template.id} className="p-3 bg-[#121212] border border-[#333333] rounded-md cursor-pointer hover:border-blue-600 hover:shadow-sm transition-all" onClick={() => loadWorkflowTemplate(template.id)} data-id="element-2522">
                      <div className="flex items-center" data-id="element-2523">
                        <div className="text-white p-2 rounded-md mr-3" style={{
                    backgroundColor: template.color
                  }} data-id="element-2524">
                          {template.icon}
                        </div>
                        <div data-id="element-2525">
                          <div className="font-medium text-sm text-white" data-id="element-2526">
                            {template.name}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-2527">
                            {template.description}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-end" data-id="element-2528">
                        <button className="text-xs text-blue-400 flex items-center hover:text-blue-300" data-id="element-2529">
                          <CopyIcon size={12} className="mr-1" data-id="element-2530" />
                          Use Template
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
          {/* Footer */}
          <div className="p-3 border-t border-[#222222]" data-id="element-2531">
            <div className="flex items-center justify-between text-xs text-gray-400" data-id="element-2532">
              <div className="flex items-center" data-id="element-2533">
                <FolderIcon size={14} className="mr-1" data-id="element-2534" />
                <span data-id="element-2535">My Workflows</span>
              </div>
              <div className="flex items-center" data-id="element-2536">
                <StarIcon size={14} className="mr-1" data-id="element-2537" />
                <span data-id="element-2538">Favorites</span>
              </div>
            </div>
          </div>
        </div>
        {/* Flow canvas */}
        <div className="flex-1 bg-[#0F0F0F]" ref={reactFlowWrapper} data-id="element-2539">
          <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onInit={setReactFlowInstance} onDrop={onDrop} onDragOver={onDragOver} nodeTypes={nodeTypes} fitView fitViewOptions={{
          padding: 0.2
        }} snapToGrid snapGrid={[15, 15]} defaultEdgeOptions={{
          type: 'smoothstep',
          style: {
            strokeWidth: 2
          }
        }} connectionLineStyle={{
          stroke: '#3b82f6',
          strokeWidth: 2
        }} connectionLineType="smoothstep" data-id="element-2540">
            <Background color="#333" gap={16} data-id="element-2541" />
            <Controls style={{
            background: '#121212',
            borderColor: '#333',
            color: 'white'
          }} data-id="element-2542" />
            <MiniMap nodeStrokeWidth={3} zoomable pannable style={{
            background: '#121212',
            borderColor: '#333',
            maskColor: 'rgba(0, 0, 0, 0.4)'
          }} nodeColor={node => {
            return node.data?.color || '#fff';
          }} data-id="element-2543" />
            <Panel position="top-right" className="bg-[#121212] p-2 rounded-md shadow-md border border-[#333333]" data-id="element-2544">
              <button className="flex items-center justify-center w-8 h-8 bg-blue-900 text-blue-300 rounded-full hover:bg-blue-800" data-id="element-2545">
                <PlusIcon size={20} data-id="element-2546" />
              </button>
            </Panel>
          </ReactFlow>
        </div>
      </div>
      {/* CSS Styles */}
      <style jsx global data-id="element-2547">{`
        .node-container {
          border-width: 2px;
          border-style: solid;
          border-radius: 8px;
          width: 200px;
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          transition: all 0.2s;
        }
        .node-container:hover {
          transform: translateY(-2px);
          box-shadow:
            0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .node-header {
          display: flex;
          align-items: center;
          padding: 10px;
          color: white;
        }
        .node-icon {
          margin-right: 8px;
        }
        .node-title {
          font-weight: 600;
          font-size: 14px;
        }
        .node-name {
          padding: 10px;
          font-size: 13px;
          color: #333;
        }
        .node-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 10px;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }
        .node-button {
          background-color: rgba(0, 0, 0, 0.1);
          color: #555;
          border-radius: 4px;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .node-button:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .node-id {
          font-size: 10px;
          color: #666;
        }
        .react-flow__handle {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #555;
        }
        .react-flow__handle:hover {
          background-color: #3b82f6;
        }
        .react-flow__edge-path {
          stroke-width: 2;
        }
        .react-flow__edge-text {
          font-size: 12px;
          fill: white;
          text-shadow: 0px 0px 2px rgba(0, 0, 0, 0.5);
        }
        .react-flow__node-custom {
          font-size: 12px;
        }
        .react-flow__controls button {
          border-color: #333;
          color: white;
        }
        .react-flow__controls button:hover {
          background-color: #333;
        }
        .react-flow__minimap {
          border-radius: 6px;
          overflow: hidden;
        }
      `}</style>
    </div>;
};
// Wrap the component with ReactFlowProvider
export function WorkflowBuilder() {
  return <div className="h-screen bg-black text-white" data-id="element-2548">
      <ReactFlowProvider data-id="element-2549">
        <FlowBuilder data-id="element-2550" />
      </ReactFlowProvider>
    </div>;
}